import { useId, useState, type JSX } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import type { ColumnDef, FilterState, FilterValue } from '@/shared/types/table-filters.types';
import { SearchInput } from './SearchInput';
import { TableFilterPanel } from './table-filters/TableFilterPanel';

export interface DataTableFiltersProps<T> {
  columns: ColumnDef<T>[];
  filters: FilterState;
  activeFilterCount: number;
  onFilterChange: (key: string, value: FilterValue | undefined) => void;
  onClearFilters: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  title?: string;
}

const PANEL_LAYOUT = 'flex flex-col gap-4 overflow-y-auto px-4 pb-4';

function FiltersTrigger({ title, count }: { title: string; count: number }): JSX.Element {
  return (
    <SheetTrigger asChild>
      <Button variant="outline" className="shrink-0">
        <SlidersHorizontal className="mr-2 h-4 w-4" />
        {title}
        {count > 0 && <Badge className="ml-2">{count}</Badge>}
      </Button>
    </SheetTrigger>
  );
}

/**
 * List toolbar: a search box plus a button that opens the column filters in a drawer.
 * Search and filter state are owned by the caller (see `useTableFilters`).
 */
export function DataTableFilters<T>({
  columns,
  filters,
  activeFilterCount,
  onFilterChange,
  onClearFilters,
  searchTerm,
  onSearchChange,
  title = 'Filters',
}: DataTableFiltersProps<T>): JSX.Element {
  const id = useId();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  return (
    <div className="flex items-center gap-2" data-testid="table-toolbar">
      <SearchInput value={searchTerm} onChange={onSearchChange} />

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <FiltersTrigger title={title} count={activeFilterCount} />

        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className="sr-only">
              Narrow the list down to the rows you care about.
            </SheetDescription>
          </SheetHeader>

          <TableFilterPanel
            columns={columns}
            filters={filters}
            scope={id}
            className={PANEL_LAYOUT}
            onFilterChange={onFilterChange}
          />

          {activeFilterCount > 0 && (
            <div className="px-4 pb-4">
              <Button variant="ghost" onClick={onClearFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
